import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import apiClient, { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { PaymentRequest, PaymentRequestMarkPaidForm } from '../../types/api';

interface MarkPaidModalProps {
  request: PaymentRequest;
  onClose: () => void;
  onSuccess: () => void;
}

const MarkPaidModal: React.FC<MarkPaidModalProps> = ({ request, onClose, onSuccess }) => {
  const [paymentReference, setPaymentReference] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!confirmChecked) {
      setError('Please confirm that payment has been completed');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const markPaidData: PaymentRequestMarkPaidForm = {
        payment_reference: paymentReference.trim() || undefined,
      };

      await apiClient.post(`/payment-requests/${request.id}/mark-paid`, markPaidData);
      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to mark payment as paid'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <CheckCircleIcon className="h-6 w-6 mr-2 text-green-600" />
            Mark Payment as Paid
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Request Details */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Amount:</span>
            <span className="font-bold text-lg">₹{request.amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Description:</span>
            <span className="text-sm font-medium">{request.description}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">UPI ID:</span>
            <span className="text-sm font-mono">{request.upi_id}</span>
          </div>
          {request.name_on_upi && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Name on UPI:</span>
              <span className="text-sm font-medium">{request.name_on_upi}</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Transaction ID / Payment Reference (Optional)</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              className="form-input"
              placeholder="Enter transaction ID from UPI app"
            />
            <p className="mt-1 text-xs text-gray-500">
              You can find this in your UPI app after completing the payment
            </p>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="confirm"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              className="mt-1 mr-2"
            />
            <label htmlFor="confirm" className="text-sm text-gray-700">
              I confirm that I have completed the payment via UPI app
            </label>
          </div>

          {/* Info Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This will automatically create an ExpenseLog entry for this payment request.
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-touch bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !confirmChecked}
              className="flex-1 btn-touch bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Processing...</span>
                </>
              ) : (
                'Mark as Paid'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarkPaidModal;

